import { AddIcon } from "@minket/brand/icons/add";
import { OauthIcon } from "@minket/brand/icons/oauth";
import { Button } from "@minket/ui/components/button";
import { EmptyState } from "@minket/ui/components/empty-state";
import { Sealed } from "@minket/ui/components/sealed";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@minket/ui/components/table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Suspense, useState } from "react";
import { stamp } from "@/components/domains/format";
import { type OauthClientBody, OauthClientDialog } from "@/components/mailboxes/dialogs";
import type { OauthClient } from "@/components/mailboxes/model";
import { UnavailableAction } from "@/components/page/action";
import { FadeText } from "@/components/page/fade-text";
import { GhostTable } from "@/components/page/ghost";
import { SubPage } from "@/components/page/page";
import { ProjectScope } from "@/components/page/scope";
import { Seams } from "@/components/page/seams";
import { QueryView } from "@/components/page/states";
import { describeError } from "@/lib/api";
import { endpoints } from "@/lib/endpoints";
import { useCan } from "@/lib/permissions";
import { apiKey, apiQuery, apiRequest } from "@/lib/query";
import { notify } from "@/lib/toast";
import { useWorkspace } from "@/lib/workspace";

export const Route = createFileRoute("/settings/oauth")({
  component: OauthSettings,
});

const columns = ["Provider", "Client id", "Redirect URI", "Added, UTC"] as const;
const will = "Each client will be listed here with its provider, its id and its redirect URI.";
const providerNames = { google: "Google", microsoft: "Microsoft" } as const;

function OauthSettings() {
  const { project } = useWorkspace();
  const projectId = project?.id ?? "";
  const queryClient = useQueryClient();
  const list = useQuery({ ...apiQuery(endpoints.oauthClients, { projectId }), enabled: !!project });
  const [adding, setAdding] = useState(false);
  const canManage = useCan("mailboxes.manage");
  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: apiKey(endpoints.oauthClients, { projectId }) });
  const save = useMutation({
    mutationFn: (body: OauthClientBody) =>
      apiRequest(endpoints.saveOauthClient, { projectId }, { body }),
    onSuccess: (client) => {
      refresh();
      setAdding(false);
      notify("success", `${providerNames[client.provider]} client saved; its secret is sealed.`);
    },
  });
  const remove = useMutation({
    mutationFn: (client: OauthClient) =>
      apiRequest(endpoints.deleteOauthClient, { projectId, clientId: client.id }),
    onSuccess: (_, client) => {
      refresh();
      void queryClient.invalidateQueries({ queryKey: apiKey(endpoints.mailboxes, { projectId }) });
      notify("success", `${providerNames[client.provider]} client removed.`);
    },
    onError: (error) => notify("error", describeError(error).message),
  });
  const preview = (busy: boolean) => (
    <GhostTable columns={columns} label="OAuth clients" busy={busy} />
  );

  return (
    <SubPage
      title="OAuth clients"
      action={
        !project || !canManage ? (
          <UnavailableAction
            label="Add client"
            icon={AddIcon}
            reason={project ? "Viewers cannot add clients" : "Pick a project first"}
          />
        ) : (
          <Button
            onClick={() => {
              save.reset();
              setAdding(true);
            }}
          >
            <AddIcon className="size-5" />
            Add client
          </Button>
        )
      }
    >
      <ProjectScope will={will} icon={<OauthIcon />} preview={preview} headingLevel={3}>
        {() => (
          <QueryView query={list} noun="OAuth clients" preview={preview} headingLevel={3}>
            {(page) =>
              page.data.length === 0 ? (
                <EmptyState
                  data-state="empty"
                  icon={<OauthIcon />}
                  headingLevel={3}
                  title="No OAuth clients yet"
                  description="Until you add one, Gmail and Microsoft 365 mailboxes consent through Minket's own clients."
                  preview={preview(false)}
                />
              ) : (
                <div className="grid min-w-0 gap-6">
                  <ClientTable
                    items={page.data}
                    canManage={canManage}
                    busy={remove.isPending}
                    onRemove={(client) => remove.mutate(client)}
                  />
                  <Sealed
                    size="compact"
                    title="Client secrets are sealed."
                    className="max-w-xl justify-items-start"
                  >
                    Stored encrypted and never shown again; save a provider's client again to
                    replace it. Removing a client disconnects the mailboxes that consented through
                    it.
                  </Sealed>
                </div>
              )
            }
          </QueryView>
        )}
      </ProjectScope>
      {adding ? (
        <Suspense fallback={null}>
          <OauthClientDialog
            pending={save.isPending}
            error={save.error}
            onSave={(body) => save.mutate(body)}
            onClose={() => setAdding(false)}
          />
        </Suspense>
      ) : null}
    </SubPage>
  );
}

function ClientTable({
  items,
  canManage,
  busy,
  onRemove,
}: {
  items: OauthClient[];
  canManage: boolean;
  busy: boolean;
  onRemove: (client: OauthClient) => void;
}) {
  const [confirming, setConfirming] = useState<string | null>(null);
  return (
    <Table aria-label="OAuth clients of this project" className="min-w-[40rem]">
      <TableHeader>
        <TableRow>
          {columns.map((column) => (
            <TableHead key={column}>{column}</TableHead>
          ))}
          <TableHead>
            <span className="sr-only">Actions</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((client) => (
          <TableRow key={client.id} className="[&>td]:align-middle">
            <TableCell className="whitespace-nowrap">
              <span className="font-semibold">{providerNames[client.provider]}</span>
              {client.tenant ? (
                <span className="block text-xs text-muted-foreground">
                  Tenant <span className="type-data">{client.tenant}</span>
                </span>
              ) : null}
            </TableCell>
            <TableCell className="type-data">
              <Seams>{client.client_id}</Seams>
            </TableCell>
            <TableCell className="max-w-0 min-w-48">
              <FadeText className="type-data">{client.redirect_uri}</FadeText>
            </TableCell>
            <TableCell className="type-data whitespace-nowrap text-muted-foreground">
              {stamp(client.created_at)}
            </TableCell>
            <TableCell>
              {!canManage ? null : confirming === client.id ? (
                <span className="flex justify-end gap-1.5">
                  <Button
                    variant="destructive"
                    size="sm"
                    loading={busy}
                    onClick={() => onRemove(client)}
                  >
                    Remove now
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setConfirming(null)}>
                    Keep
                  </Button>
                </span>
              ) : (
                <span className="flex justify-end">
                  <Button variant="ghost" size="sm" onClick={() => setConfirming(client.id)}>
                    Remove <span className="sr-only">{providerNames[client.provider]} client</span>
                  </Button>
                </span>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
